import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

export interface VariantRow {
  id?: string;
  size?: string;
  color?: string;
  price: number;
  originalPrice: number;
  quantityAvailable: number;
  sku?: string;
}

export interface VariantConfig {
  hasSizes: boolean;
  hasColors: boolean;
  sizes: string[];
  colors: string[];
}

interface ProductVariantEditorProps {
  enabled: boolean;
  config: VariantConfig;
  variants: VariantRow[];
  basePrice: number;
  baseOriginalPrice: number;
  onConfigChange: (config: VariantConfig) => void;
  onVariantsChange: (variants: VariantRow[]) => void;
  onRequestEnable?: () => void;
}

function uniqueList(values: string[]): string[] {
  const seen = new Set<string>();
  return values
    .map((v) => v.trim())
    .filter((v) => {
      if (!v) return false;
      const key = v.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function buildCombinations(config: VariantConfig): Array<{ size?: string; color?: string }> {
  const sizes = config.hasSizes ? config.sizes : [];
  const colors = config.hasColors ? config.colors : [];
  if (!config.hasSizes && !config.hasColors) return [{}];
  if (config.hasSizes && !config.hasColors) return sizes.map((size) => ({ size }));
  if (!config.hasSizes && config.hasColors) return colors.map((color) => ({ color }));
  const combos: Array<{ size?: string; color?: string }> = [];
  for (const size of sizes) {
    for (const color of colors) {
      combos.push({ size, color });
    }
  }
  return combos;
}

const ProductVariantEditor: React.FC<ProductVariantEditorProps> = ({
  enabled,
  config,
  variants,
  basePrice,
  baseOriginalPrice,
  onConfigChange,
  onVariantsChange,
  onRequestEnable,
}) => {
  const { t } = useTranslation('addProduct');
  const [sizeInput, setSizeInput] = useState('');
  const [colorInput, setColorInput] = useState('');

  const combinations = useMemo(() => buildCombinations(config), [config]);

  useEffect(() => {
    if (!enabled) return;
    const map = new Map(
      variants.map((v) => [`${(v.size || '').toLowerCase()}::${(v.color || '').toLowerCase()}`, v])
    );
    const next = combinations.map((combo) => {
      const key = `${(combo.size || '').toLowerCase()}::${(combo.color || '').toLowerCase()}`;
      const existing = map.get(key);
      return {
        ...combo,
        id: existing?.id,
        price: existing?.price ?? basePrice,
        originalPrice: existing?.originalPrice ?? baseOriginalPrice,
        quantityAvailable: existing?.quantityAvailable ?? 0,
        sku: existing?.sku,
      } as VariantRow;
    });
    if (JSON.stringify(next) !== JSON.stringify(variants)) {
      onVariantsChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, combinations, basePrice, baseOriginalPrice]);

  if (!enabled) {
    return (
      <div className="border border-dashed border-gray-300 rounded-xl p-4 md:p-6 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('variants.title')}</h3>
        <p className="text-sm text-gray-600 mb-4">{t('variants.disabledHint')}</p>
        {onRequestEnable && (
          <button type="button" className="btn-primary" onClick={onRequestEnable}>
            {t('variants.enableButton')}
          </button>
        )}
      </div>
    );
  }

  const updateVariant = (index: number, patch: Partial<VariantRow>) => {
    const next = [...variants];
    next[index] = { ...next[index], ...patch };
    onVariantsChange(next);
  };

  const addSize = () => {
    const value = sizeInput.trim();
    if (!value) return;
    onConfigChange({
      ...config,
      sizes: uniqueList([...config.sizes, value]),
    });
    setSizeInput('');
  };

  const addColor = () => {
    const value = colorInput.trim();
    if (!value) return;
    onConfigChange({
      ...config,
      colors: uniqueList([...config.colors, value]),
    });
    setColorInput('');
  };

  return (
    <div className="space-y-6 border border-gray-200 rounded-xl p-4 md:p-6 bg-gray-50">
      <h3 className="text-lg font-semibold text-gray-900">{t('variants.title')}</h3>

      <div className="flex flex-wrap gap-6">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={config.hasSizes}
            onChange={(e) =>
              onConfigChange({
                ...config,
                hasSizes: e.target.checked,
                sizes: e.target.checked ? config.sizes : [],
              })
            }
          />
          {t('variants.hasSizes')}
        </label>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={config.hasColors}
            onChange={(e) =>
              onConfigChange({
                ...config,
                hasColors: e.target.checked,
                colors: e.target.checked ? config.colors : [],
              })
            }
          />
          {t('variants.hasColors')}
        </label>
      </div>

      {config.hasSizes && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('variants.sizes')}</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input-field flex-1"
              placeholder={t('variants.sizePlaceholder')}
              value={sizeInput}
              onChange={(e) => setSizeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSize())}
            />
            <button type="button" className="btn-secondary px-4" onClick={addSize}>
              <Plus size={18} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {config.sizes.map((size) => (
              <span
                key={size}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border text-sm"
              >
                {size}
                <button
                  type="button"
                  className="text-red-500"
                  onClick={() =>
                    onConfigChange({
                      ...config,
                      sizes: config.sizes.filter((s) => s !== size),
                    })
                  }
                >
                  <Trash2 size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {config.hasColors && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('variants.colors')}</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input-field flex-1"
              placeholder={t('variants.colorPlaceholder')}
              value={colorInput}
              onChange={(e) => setColorInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addColor())}
            />
            <button type="button" className="btn-secondary px-4" onClick={addColor}>
              <Plus size={18} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {config.colors.map((color) => (
              <span
                key={color}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border text-sm"
              >
                {color}
                <button
                  type="button"
                  className="text-red-500"
                  onClick={() =>
                    onConfigChange({
                      ...config,
                      colors: config.colors.filter((c) => c !== color),
                    })
                  }
                >
                  <Trash2 size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('variants.defaultPrice')}</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="input-field"
            value={basePrice}
            readOnly
          />
        </div>
        <p className="text-sm text-gray-500 md:col-span-2">
          {t('variants.combinationHint', { count: combinations.length })}
        </p>
      </div>

      {combinations.length > 0 && (config.hasSizes || config.hasColors) && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-lg border">
            <thead className="bg-gray-100 text-left">
              <tr>
                {config.hasSizes && <th className="p-3">{t('variants.tableSize')}</th>}
                {config.hasColors && <th className="p-3">{t('variants.tableColor')}</th>}
                <th className="p-3">{t('variants.tablePrice')}</th>
                <th className="p-3">{t('variants.tableCompareAt')}</th>
                <th className="p-3">{t('variants.tableStock')}</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((row, index) => (
                <tr key={`${row.size}-${row.color}-${index}`} className="border-t">
                  {config.hasSizes && <td className="p-3">{row.size || '—'}</td>}
                  {config.hasColors && <td className="p-3">{row.color || '—'}</td>}
                  <td className="p-3">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-field py-2"
                      value={row.price}
                      onChange={(e) =>
                        updateVariant(index, { price: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </td>
                  <td className="p-3">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-field py-2"
                      value={row.originalPrice}
                      onChange={(e) =>
                        updateVariant(index, {
                          originalPrice: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </td>
                  <td className="p-3">
                    <input
                      type="number"
                      min="0"
                      className="input-field py-2"
                      value={row.quantityAvailable}
                      onChange={(e) =>
                        updateVariant(index, {
                          quantityAvailable: parseInt(e.target.value, 10) || 0,
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductVariantEditor;
